import React, { useEffect, useRef } from 'react';
import { Typography, Button, Space, Card, message } from 'antd';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CodeOutlined } from '@ant-design/icons';

const { Title, Paragraph } = Typography;

export const HomePage: React.FC = () => {
  const { isAuthenticated, login, loginAsGuest } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const guestLoginInitiated = useRef(false);

  useEffect(() => {
    const handleGuestLogin = async () => {
      const searchParams = new URLSearchParams(location.search);
      const user = searchParams.get('user');
      const password = searchParams.get('password');

      if (user === 'guest' && password && !guestLoginInitiated.current && !isAuthenticated) {
        guestLoginInitiated.current = true;
        try {
          const result = await login('guest@example.com', password);
          if (result.success) {
            message.success('Guest login successful!');
            const nextUrl = searchParams.get('next');
            const redirectUrl = searchParams.get('redirect');
            navigate((nextUrl || (redirectUrl ? `/${redirectUrl}`: null)) || '/dashboard');
          } else {
            message.error(result.message || 'Guest login failed. Please check your credentials.');
            // Clear the URL parameters on failure
            navigate('/', { replace: true });
          }
        } catch (error) {
          message.error('An unexpected error occurred during guest login.');
          navigate('/', { replace: true });
        }
      }
    };

    handleGuestLogin();
  }, [location.search, login, navigate, isAuthenticated]);

  const handleGuestLoginButton = async (redirectPath: string) => {
    try {
      await loginAsGuest();
      navigate(redirectPath);
    } catch (error) {
      console.error('Guest login failed', error);
    }
  };

  return (
    <div style={{ padding: '0 24px' }}>
      {/* Hero Section */}
      <div style={{ textAlign: 'center', paddingTop: '50px', paddingBottom: '50px' }}>
        <Title level={1} style={{ marginBottom: '20px' }}>
          Welcome to Gudu Portal
        </Title>
        {!isAuthenticated && (
          <Button type="primary" size="large" onClick={() => navigate('/login')}>
            Sign In
          </Button>
        )}
      </div>

      {/* Featured Tools Section */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '50px', paddingBottom: '50px' }}>
        <Card 
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CodeOutlined style={{ fontSize: '24px', marginRight: '10px' }} />
              <Title level={4} style={{ margin: 0 }}>Featured Tool: Get Table Column</Title>
            </div>
          }
          style={{ width: 550, textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
        >
          <Paragraph style={{ fontSize: '16px', color: '#555' }}>
            Instantly analyze your SQL scripts to identify all table columns. 
            This tool is perfect for impact analysis and understanding data lineage.
          </Paragraph>
          {isAuthenticated ? (
            <Button type="primary" size="large">
              <Link to="/get-table-column">Try the Tool Now</Link>
            </Button>
          ) : (
            <Button type="primary" size="large" onClick={() => handleGuestLoginButton('/get-table-column')}>
              Login as Guest & Try the Tool
            </Button>
          )}
        </Card>

        <Card 
          title={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CodeOutlined style={{ fontSize: '24px', marginRight: '10px' }} />
              <Title level={4} style={{ margin: 0 }}>Featured Tool: SQL to XML</Title>
            </div>
          }
          style={{ width: 550, textAlign: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
        >
          <Paragraph style={{ fontSize: '16px', color: '#555' }}>
            Convert your SQL scripts to XML format.
            This tool is useful for data interchange and integration tasks.
          </Paragraph>
          {isAuthenticated ? (
            <Button type="primary" size="large">
              <Link to="/to-xml">Try the Tool Now</Link>
            </Button>
          ) : (
            <Button type="primary" size="large" onClick={() => handleGuestLoginButton('/to-xml')}>
              Login as Guest & Try the Tool
            </Button>
          )}
        </Card>
      </div>
    </div>
  );
}; 